import { PaidOutlined } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { useHistory } from 'react-router';

import Button from 'domains/core/components/Button';
import EmptyStateLayout from 'domains/core/components/EmptyStateLayout';
import PageLayout from 'domains/core/components/PageLayout';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import UpdateTag from 'domains/core/components/UpdateTag';
import ValueCalculatorTable from 'domains/value/pages/ValueCalculatorTable';
import { reset } from 'domains/value/state/value';
import useClickApptValueAssumption from 'hooks/queries/useClickApptValueAssumption';
import { useAppDispatch } from 'hooks/redux';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import { URLPaths } from 'models/enums';

const ValueCalculatorPage = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { canCreateValue } = useAccessCentrePermissions();
    const {
        data: clickApptValueAssumption,
        error: clickApptValueAssumptionError,
        isError: isClickApptValueAssumptionError,
        isLoading: isClickApptValueAssumptionLoading,
        isRefetching: isClickApptValueAssumptionRefetching,
        isSuccess: isClickApptValueAssumptionSuccess,
        refetch: clickApptValueAssumptionRefetch,
    } = useClickApptValueAssumption();

    const onClickCreate = () => {
        history.push(URLPaths.VALUE_SEND_SPECIALTY);
        dispatch(reset());
    };

    const isEmpty = isClickApptValueAssumptionSuccess && !clickApptValueAssumption?.length;

    const createButton = canCreateValue ? (
        <Button variant="outlined" endIcon={<PaidOutlined />} onClick={onClickCreate}>
            Create
        </Button>
    ) : undefined;

    if (isEmpty) {
        return (
            <EmptyStateLayout caption="value" header="track the impact of your marketing efforts">
                {createButton}
            </EmptyStateLayout>
        );
    }

    const retirementNotice = (
        <UpdateTag
            tag="Feature Update - Value Calculator"
            header="What's Changing"
            body={
                <Stack rowGap={2}>
                    <Typography variant="body2">
                        We're reimagining how our customers measure success. To make room for a more robust and accurate
                        ROI &amp; Attribution functionality, we will be retiring the current Value Calculator at the end
                        of Q2.
                    </Typography>
                    <Typography variant="h3">What it means for you</Typography>
                    <Stack component="ul" rowGap={1} sx={{ pl: 2, m: 0 }}>
                        <Typography component="li" variant="body2">
                            If you have historical calculations or data within the Value Calculator that you'd like to
                            keep, please export your Value Dashboard before the end of the quarter.
                        </Typography>
                        <Typography component="li" variant="body2">
                            Have feedback you want to share on how we do value calculations? Reach out to your customer
                            success team and we'll set up a feedback session.
                        </Typography>
                    </Stack>
                </Stack>
            }
        />
    );

    return (
        <PageLayout
            header="value"
            headerIcon={<PaidOutlined fontSize="inherit" />}
            headerAction={createButton}
            headerOthers={retirementNotice}
        >
            {isClickApptValueAssumptionError && <ErrorSnackbar errorMessage={clickApptValueAssumptionError?.message} />}
            {(isClickApptValueAssumptionLoading || isClickApptValueAssumptionSuccess) && (
                <ValueCalculatorTable
                    clickApptValueAssumptionResponses={clickApptValueAssumption ?? []}
                    isLoading={isClickApptValueAssumptionLoading}
                    isRefetching={isClickApptValueAssumptionRefetching}
                    refetch={clickApptValueAssumptionRefetch}
                />
            )}
        </PageLayout>
    );
};

export default ValueCalculatorPage;
